import React, { useState, useRef, useEffect, useCallback } from 'react';
import MapView from './MapView';
import TileSelect from '../map-plugins/TileSelect';
import ProfileSelect from '../map-plugins/ProfileSelect';
import LayerSearch from '../map-plugins/LayerSearch';
import {AppActions} from '../../context/AppContext';
import {Space} from 'antd';
import '../../styles/side-by-side.css';

export default function SideBySideMapView({
    baseProps,
    compareProps,
    mapSize,
    onTypeChange,
    hasCompareData,
    sharedTile,
    onTileChange,
    tiles,
    mapData,
    dispatchProject
}) {
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const [profile, setProfile] = useState(null);
    const containerRef = useRef(null);
    const baseMapRef = useRef(null);
    const compareMapRef = useRef(null);
    const isSyncing = useRef(false);

    // Handle layer search - navigate both maps to selected location
    const handleLayerSearch = useCallback((item) => {
        if (!item) return;
        
        // Set selected RS to highlight on both maps
        if (item.type === 'cross_section') {
            dispatchProject({
                type: AppActions.SET_SELECTED_RS,
                data: {rs: item._text}
            });
        }
        
        // Navigate map if xy coordinates exist
        if (item?.xy && item.xy.length && item.xy[0]) {
            try {
                const center = [item.xy[0][1], item.xy[0][0]];
                if (baseMapRef.current) {
                    baseMapRef.current.setView(center, 16, {animate: false});
                }
                // Compare map will sync automatically via syncMaps
            } catch (e) {
                console.error('LayerSearch navigation error:', e);
            }
        }
    }, [dispatchProject]);

    // Load toolbar for side-by-side mode
    useEffect(() => {
        dispatchProject({
            type: AppActions.SET_MESSAGE_TOOLBAR_ITEM,
            data: {
                toolbar: (
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <TileSelect value={sharedTile} onChange={onTileChange} tiles={tiles}/>
                        <ProfileSelect profiles={mapData?.profiles} onChangeProfile={setProfile} selectedProfile={profile} />
                        <Space.Compact size={'small'}>
                            <LayerSearch onChange={handleLayerSearch} type="base"/>
                        </Space.Compact>
                    </div>
                )
            }
        })
        
        return () => {
            dispatchProject({
                type: AppActions.SET_MESSAGE_TOOLBAR_ITEM,
                data: {toolbar: null}
            })
        }
    }, [sharedTile, tiles, mapData, profile, dispatchProject, onTileChange, handleLayerSearch]);

    // Sync maps when one moves
    const syncMaps = useCallback((sourceMap, targetMap) => {
        if (isSyncing.current) return;
        if (!sourceMap || !targetMap) return;
        
        isSyncing.current = true;
        const center = sourceMap.getCenter();
        const zoom = sourceMap.getZoom();
        targetMap.setView(center, zoom, { animate: false });
        setTimeout(() => {
            isSyncing.current = false;
        }, 10);
    }, []);

    // Setup sync listeners
    const setupMapSync = useCallback(() => {
        const baseMap = baseMapRef.current;
        const compareMap = compareMapRef.current;

        if (!baseMap || !compareMap) return;

        const onBaseMove = () => syncMaps(baseMap, compareMap);
        const onCompareMove = () => syncMaps(compareMap, baseMap);

        baseMap.on('move', onBaseMove);
        baseMap.on('zoom', onBaseMove);
        compareMap.on('move', onCompareMove);
        compareMap.on('zoom', onCompareMove);

        // Initial sync
        syncMaps(baseMap, compareMap);

        return () => {
            baseMap.off('move', onBaseMove);
            baseMap.off('zoom', onBaseMove);
            compareMap.off('move', onCompareMove);
            compareMap.off('zoom', onCompareMove);
        };
    }, [syncMaps]);

    // Handle slider drag
    const handleMouseDown = (e) => {
        setIsDragging(true);
        e.preventDefault();
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;
        
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
        setSliderPosition(percentage);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle touch events
    const handleTouchStart = (e) => {
        setIsDragging(true);
    };

    const handleTouchMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;
        
        const touch = e.touches[0];
        const rect = containerRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
        setSliderPosition(percentage);
    }, [isDragging]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

    // Keyboard listener for B/C/S
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
                return;
            }
            const key = e.key.toLowerCase();
            if (key === 'b') {
                onTypeChange('base');
            } else if (key === 'c') {
                onTypeChange('compare');
            } else if (key === 's') {
                onTypeChange('sidebyside');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onTypeChange]);

    const size = mapSize || { height: window.innerHeight - 125, width: window.innerWidth - 2 };

    return (
        <div 
            ref={containerRef}
            className="sbs-container"
            style={{ 
                position: 'relative', 
                width: size.width, 
                height: size.height,
                overflow: 'hidden'
            }}
        >
            {/* Base map (left side) */}
            <div 
                className="sbs-map sbs-base-map"
                style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                    zIndex: 1
                }}
            >
                <MapView
                    {...baseProps}
                    mapSize={size}
                    sharedTile={sharedTile}
                    onSharedTileChange={onTileChange}
                    onMapReady={(map) => {
                        baseMapRef.current = map;
                        setupMapSync();
                    }}
                />
            </div>

            {/* Compare map (right side) */}
            <div 
                className="sbs-map sbs-compare-map"
                style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    clipPath: `inset(0 0 0 ${sliderPosition}%)`,
                    zIndex: 2
                }}
            >
                <MapView
                    {...compareProps}
                    mapSize={size}
                    sharedTile={sharedTile}
                    onSharedTileChange={onTileChange}
                    onMapReady={(map) => {
                        compareMapRef.current = map;
                        setupMapSync();
                    }}
                />
            </div>

            {/* Slider */}
            <div 
                className="sbs-slider"
                style={{ 
                    left: `${sliderPosition}%`,
                    zIndex: 1000
                }}
            >
                <div className="sbs-slider-line" />
                <div 
                    className="sbs-slider-handle"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                </div>
            </div>
        </div>
    );
}
